import { Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FormControl, UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { ConfirmationService, MessageService, SelectItem } from "primeng/api";
import { Message } from "@bbwt/classes";
import { UserService } from "./user.service";
import { AccountService } from "@account/services";
import { AccountStatus } from "./account-status";
import { IRole, RoleService } from "../roles";
import { getDefaultPermissions, resolvePermissionsOnRoleChange } from "./default-permission";
import { UserEditDataService } from "./user-edit-data.service";
import { IPermission } from "@main/permissions/permission";
import { UserHelperService } from "./user-helper.service";
import { LinkUserService, UserAccess, UserLogin } from ".";
import { firstValueFrom, map } from "rxjs";
import { checkGenericLoginOrClinicalSafetyOfficerRole, checkGenericLoginRole, checkGenericPatientLoginRole } from "./generic-login.utils";
import { CoreRole, FormioRole } from "@main/roles/core-role"; // Ensure FormioRole is imported
import { Permissions } from "@main/permissions/permissions";
import { MultiSelect, MultiSelectChangeEvent } from "primeng/multiselect";
import { IOrganization, OrganizationService } from "@main/companies";
import { FormButtonsConfig } from "@mipro/components";
import { SetLocalTimeZone, SetUTCTimeZone } from "@bbwt/utils";
import { NamingConventionService } from "@mipro/services/naming-convention.service";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import { UserFiltersService } from "./user-filters.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { DefaultPermissions } from "./default-permissions";
import { PermissionService } from "@main/permissions/permission.service";
import { MiProModules } from "@mipro/classes/mipro-modules";

@Component({
    selector: "edit-user",
    templateUrl: "./edit-user.component.html",
    styleUrls: ["./edit-user.component.scss"]
})
export class EditUserComponent implements OnInit {
    private readonly hiddenRoles: string[] = [CoreRole.PPS, FormioRole.FormioExternalUser];
    private hiddenRoleObjects: IRole[] = [];
    private organisationChangeVersion = 0;
    private lastRoleDefaultPermissionIds = new Set<string>();

    private userId: string;
    private currentUserName: string;
    private _prnRequired: boolean = false;
    private organizationFormPermissions: { [orgId: string]: IPermission[] };

    userForm: UntypedFormGroup = this.fb.group({
        user: [null],
        otherFields: this.fb.group({
            userAccesses: [[]],
            userName: [null],
            signatureImageId: [null],
            autoSaveForms: [false]
        }),
        isAutoSaveFormOn: [false]
    });

    loadingUser: boolean = true;
    savingUser: boolean = false;
    runningUserAction: boolean = false;
    permissionsLoading: boolean = false;
    userSsoProvider: number;
    roleOptions: IRole[] = [];
    permissionOptions: IPermission[] = [];
    defaultPermissions: DefaultPermissions;
    permissionsDependencies: { [permission: string]: string[][] };
    enableImpersonateBtn: boolean = false;
    enableApproveBtn: boolean = false;
    enableSuspendBtn: boolean = false;
    enableDeleteBtn: boolean = false;
    enableUnsuspendBtn: boolean;
    enableRestoreBtn: boolean;
    enableResendInvitationBtn: boolean;
    enableResendEmailConfirmationBtn: boolean;
    enableSendPasswordResetMailBtn: boolean;
    enableSendPinResetMailBtn: boolean;
    isGenericLogin: boolean = false;
    defaultClinicalSafetyOfficer: boolean = false;
    isClinicalSafetyOfficer: boolean = false;
    userRoles: string[] = [];
    userPermissions: string[] = [];
    displayAddOrganization: boolean = false;
    organizationsOptions: SelectItem[] = [];
    newOrganization: string = null;
    organizations: IOrganization[];
    appRoles: IRole[];

    returnUrl: string = null;
    episodeId: string = null;
    from: string = null;
    createDateTime: string = null;
    walkInDate: string = null;
    walkInId: string = null;
    warningText: string = null;

    allowedToReset2Fa: boolean = false;
    autosaveComplete: boolean = false;

    buttonsConfig = <FormButtonsConfig>{
        cancelFunc: () => this.tryGoLocationBack(true)
    };

    @ViewChild("userPermissionsMultiselect") userPermissionsMultiselectComponent: MultiSelect;

    get userOrganizationRoles() {
        return this.currentUserAccess.roles;
    }

    set userOrganizationRoles(roles: IRole[]) {
        this.currentUserAccess.roles = roles;
        this.patchUserAccesses();
    }

    get prnRequired() {
        const isDoctor = this.userOrganizationRoles?.some(x => x.name === CoreRole.Doctor);
        const hasWritePrescription = this.userPermissionsMultiselectComponent?.value?.some(x => x.name === Permissions.WritePrescriptions);
        this._prnRequired = isDoctor || hasWritePrescription;
        return this._prnRequired;
    }

    get userOrganizationPermissions() {
        return this.currentUserAccess.permissions;
    }

    set userOrganizationPermissions(permissions: IPermission[]) {
        this.currentUserAccess.permissions = permissions;
        this.syncCurrentPermissionsToUserAccesses();
        this.patchUserAccesses();
    }

    defaultGenericPatient: boolean = false;
    currentUserAccess: UserAccess = {roles: [], permissions: [], squads: []} as any;
    userAccesses: UserAccess[] = [];
    initialUserAccesses: UserAccess[] = [];

    get validMultipleOrgsRoles() {
        return this.userAccesses.findIndex(ua => !ua.roles?.length) < 0;
    }

    get validMultipleOrgsPermissions() {
        return this.userAccesses.findIndex(ua => !ua.permissions?.length) < 0;
    }

    get multipleOrganizations() {
        return (
            (this.userHelperService.isMiProAdmin ||
                this.userHelperService.isSuperAdmin ||
                this.userHelperService.hasPermission(Permissions.CreateUser)) &&
            this.userAccesses.length > 1
        );
    }

    get userName(): FormControl {
        return this.userForm.get("otherFields")?.get("userName") as FormControl;
    }

    get isAutoSaveFormOn() {
        return this.userForm.get("isAutoSaveFormOn").value;
    }

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private linkUserService: LinkUserService,
        private userService: UserService,
        private userFiltersService: UserFiltersService,
        private accountService: AccountService,
        private userHelperService: UserHelperService,
        private userEditDataService: UserEditDataService,
        private fb: UntypedFormBuilder,
        private roleService: RoleService,
        public namingConvService: NamingConventionService,
        private permissionService: PermissionService,
        broadcastService: BroadcastService,
        private organizationService: OrganizationService
    ) {
        broadcastService
            .on(UserHelperService.ClaimsUpdatedEventName)
            .pipe(takeUntilDestroyed())
            .subscribe(_ => this.initWidgets());
    }

    async ngOnInit() {
        this.userId = await firstValueFrom(this.activatedRoute.params.pipe(map(p => p["id"])));
        this.allowedToReset2Fa = this.userHelperService.isTopAdmin;

        await this.refreshUser();

        await this.initOrganizations();

        await this.initWidgets();
        this.onOrganisationChanged().then();
        this.loadingUser = false;
    }

    get isGoverningBodyAdmin(): boolean {
        return this.userRoles?.some(r => r == CoreRole.GovBodyAdmin);
    }

    private async initOrganizations() {
        let organizations = await this.userFiltersService.loadOrganizations();
        this.organizations = organizations;
        organizations = organizations.filter(o => this.userAccesses.findIndex(a => a.organization.id === o.id) < 0);

        if (!(this.userHelperService.isMiProAdmin || this.userHelperService.isSuperAdmin)) {
            let currentUserLogins = await this.linkUserService.getUserLogins();
            let currentUserOrganizations = [];
            currentUserLogins.forEach(ul => {
                currentUserOrganizations = currentUserOrganizations.concat(ul.organizations);
            });

            let currentUserOrgsCreateUserAllowed = currentUserOrganizations.filter(o => o.permissions.includes(Permissions.CreateUser));

            organizations = organizations.filter(o => currentUserOrgsCreateUserAllowed.findIndex(co => co.id === o.id) > 0);

            this.userAccesses = this.userAccesses.filter(ua => currentUserOrgsCreateUserAllowed.some(co => co.id === ua.organization.id));
        }

        if (this.isGoverningBodyAdmin) {
            const govBodyId = this.userAccesses[0]?.organization.governingBodyId;
            this.organizationsOptions = organizations
                .filter(o => o.governingBodyId == govBodyId)
                .map(o => <SelectItem>{label: `${o.governingBodyName} - ${o.name}`, value: o.id});
        } else {
            this.organizationsOptions = organizations.map(o => <SelectItem>{label: `${o.governingBodyName} - ${o.name}`, value: o.id});
        }
    }

    get isValid() {
        return (
            this.userForm.valid && this.validMultipleOrgsPermissions && this.validMultipleOrgsRoles /* && this.validMultipleOrgsGroups */
        );
    }

    async manualSave() {
        await this.save();
        this.warningText = null;
        this.tryGoLocationBack(false);
    }

    tryGoLocationBack(cancel: boolean) {
        if (cancel) {
            this.back();
        }
    }

    save = async () => {
        if (!this.isValid) {
            this.userForm.markAllAsTouched();
            return;
        }

        this.savingUser = true;
        try {
            this.syncCurrentPermissionsFromMultiselect();
            this.patchUserAccesses();

            const user = this.userForm.get("user").value;
            const otherFields = this.userForm.get("otherFields").value;
            user.dateOfBirth = SetUTCTimeZone(user.dateOfBirth);

            let userLogin: UserLogin = Object.assign({}, user, otherFields);
            userLogin.userAccesses = this.buildUserAccessesForSave();
            userLogin = await this.userService.update(this.userId, userLogin);

            if (!this.userService.currentUser?.autoSaveForms) {
                this.patchUserForm(userLogin);
            }
            this.userAccesses = userLogin.userAccesses.filter(ua => !ua.isArchived && ua.roles && ua.roles.length > 0);
            this.initialUserAccesses = [...this.userAccesses];

            this.currentUserAccess =
                this.userAccesses.find(ua => ua.organization.id === this.currentUserAccess.organization.id) ?? this.userAccesses[0];

            this.hiddenRoleObjects = this.currentUserAccess.roles.filter(r => this.hiddenRoles.includes(r.name));
            this.currentUserAccess.roles = this.currentUserAccess.roles.filter(r => !this.hiddenRoles.includes(r.name));

            return { user: userLogin, otherFields: userLogin };
        } finally {
            this.savingUser = false;
        }
    };

    back(): void {
        this.router.navigate(["/app/users"]).then();
    }

    userTypeChanged(resetPermissions = false) {
        [this.isGenericLogin, this.isClinicalSafetyOfficer] = checkGenericLoginOrClinicalSafetyOfficerRole(
            this.userName,
            this.userOrganizationRoles
        );
        this.setDefaultPermissions(resetPermissions || this.isGenericLogin || this.isClinicalSafetyOfficer);
        this.userPermissionsMultiselectComponent?.options?.forEach(x => (x.disabled = false));
        this.permissionOptions = this.createPermissionOptions();
    }

    async impersonate() {
        this.runningUserAction = true;
        await this.userService.impersonateCurrentUserAsUser(this.userId, this.currentUserAccess.organization.id);
        this.router.navigate(["/"]).then();
    }

    createRoleOptions(roles: IRole[]): void {
        this.roleOptions = this.isGoverningBodyAdmin
            ? roles?.filter(r => r.name == CoreRole.GovBodyAdmin && !this.hiddenRoles.includes(r.name)) ?? []
            : roles?.filter(r => r.name != CoreRole.GovBodyAdmin && !this.hiddenRoles.includes(r.name)) ?? [];
    }

    onCurrentUserAccessChange(newAccess: UserAccess) {
        this.syncCurrentPermissionsFromMultiselect();
        this.currentUserAccess = newAccess;
        void this.onOrganisationChanged();
    }

    async onOrganisationChanged() {
        const changeVersion = ++this.organisationChangeVersion;
        await this.updateRoles();
        if (changeVersion !== this.organisationChangeVersion) {
            return;
        }

        const org = this.currentUserAccess.organization.id;
        const orgData = this.organizations.find(c => c.id === org);
        if (orgData === undefined) {
            this.createRoleOptions(this.appRoles);
            return;
        }

        const isRavenEnabled = orgData.isRavenEnabled;
        const isDiaryEnabled = orgData.isDiaryEnabled;
        const isFunctionalGut = orgData.isFunctionalGut;
        const isFormsEnabled = orgData.isFormsEnabled;

        const disabledUserRolesForForms = [CoreRole.GenericLogin, CoreRole.GenericPatientLogin, CoreRole.ClinicalSafetyOfficer];

        this.permissionOptions = this.createPermissionOptions();
        if (!isFunctionalGut) {
            this.permissionOptions = this.permissionOptions.filter(
                x => x.name !== Permissions.WriteFunctionalGut && x.name !== Permissions.ReadFunctionalGut
            );
        }

        const deprecatedFormPermissions = ["Forms: Write", "Forms: Read"];
        this.permissionOptions = this.permissionOptions.filter(x => !deprecatedFormPermissions.includes(x.name));

        if (!isFormsEnabled || this.userHelperService.hasRole(...disabledUserRolesForForms)) {
            const formPermissions = [Permissions.WriteFormsDesign, Permissions.ReadFormsInstances, Permissions.WriteFormsInstances].map(x =>
                x.toString()
            );

            this.permissionOptions = this.permissionOptions.filter(x => !formPermissions.includes(x.name));
        }

        let filteredRoles = this.appRoles;
        [this.isGenericLogin, this.isClinicalSafetyOfficer] = checkGenericLoginOrClinicalSafetyOfficerRole(
            this.userName,
            this.userOrganizationRoles
        );
        if (!isRavenEnabled) {
            filteredRoles = filteredRoles.filter(x => x.name !== CoreRole.ClinicalSafetyOfficer || this.isClinicalSafetyOfficer);
        }
        if (!isDiaryEnabled) {
            filteredRoles = filteredRoles.filter(x => (x.name !== CoreRole.GenericLogin && x.name !== CoreRole.GenericPatientLogin) || this.isGenericLogin);
        }

        this.permissionOptions = this.createPermissionOptions();

        this.createRoleOptions(filteredRoles);

        // Keep the org's saved permissions; only refresh UI and track current role defaults for later role changes.
        this.userPermissionsMultiselectComponent?.options?.forEach(x => (x.disabled = false));
        this.seedLastRoleDefaultPermissionIds();

        this.defaultGenericPatient = checkGenericPatientLoginRole(this.userName, this.userOrganizationRoles);
        this.defaultClinicalSafetyOfficer = this.isClinicalSafetyOfficer = this.userOrganizationRoles?.some(
            r => r.name === CoreRole.ClinicalSafetyOfficer
        );
    }

    resendInvitation() {
        this.confirmationService.confirm({
            message: "Are you sure want to send invitation to selected user again?",
            accept: async () => {
                await this.runUserAction(() => this.userService.resendInvitation(this.userId));
                this.messageService.add(Message.Success("The invitation has been sent.", "Invitation"));
            }
        });
    }

    resendEmailConfirmation() {
        this.confirmationService.confirm({
            message: "Are you sure want send email confirmation to selected user again?",
            accept: async () => {
                await this.runUserAction(() => this.userService.resendEmailConfirmation(this.userId));
                this.messageService.add(Message.Success("The email confirmation has been repeatedly sent.", "Email Confirmation"));
            }
        });
    }

    reset2FaConfirmation() {
        this.confirmationService.confirm({
            message: "Are you sure want to reset user's 2FA?",
            accept: async () => {
                await this.runUserAction(() => this.accountService.disable2faForUser(this.userId));
                this.messageService.add(Message.Success("The 2FA has been reset.", "Reset 2FA"));
            }
        });
    }

    protected unlinkOrganization() {
        this.confirmationService.confirm({
            message: "Are you sure that you want to unlink this organisation?",
            accept: async () => {
                try {
                    this.loadingUser = true;
                    if (this.initialUserAccesses.find(x => x.organization.id == this.currentUserAccess.organization.id)) {
                        await this.linkUserService.delinkOrganization(this.userId, this.currentUserAccess.organization.id);
                        await this.refreshUser();
                        await this.initOrganizations();
                        await this.initWidgets();
                    } else {
                        this.removeOrganization();
                    }

                    this.onOrganisationChanged().then();
                    this.messageService.add(Message.Success("Organisation successfully unlinked"));
                    this.loadingUser = false;
                } catch {
                }
            }
        });
    }

    async sendPasswordResetMail() {
        const currentUserName = (await this.userService.get(this.userId)).userName ?? "";
        await this.runUserAction(() => this.accountService.recoverPassword({ email: currentUserName }));
        this.messageService.add(Message.Success("The email has been sent.", "Password Reset"));
    }

    async sendOfflinePinResetMail() {
        await this.runUserAction(() => this.accountService.recoverOfflinePin({ email: this.currentUserName }));
        this.messageService.add(Message.Success("The email has been sent.", "Offline PIN Reset"));
    }

    async approve() {
        await this.runUserAction(() => this.userService.approve(this.userId));
        await this.refreshUser();
    }

    async toggleLocking() {
        await this.runUserAction(() => this.userService.toggleLocking(this.userId));
        await this.refreshUser();
    }

    async toggleDeleting() {
        return this.enableDeleteBtn
            ? this.deleteWithConfirmation(async () => {
                await this.runUserAction(() => this.userService.toggleDeleting(this.userId));
                await this.refreshUser();
            })
            : this.restoreWithConfirmation(async () => {
                await this.runUserAction(() => this.userService.toggleDeleting(this.userId));
                await this.refreshUser();
            });
    }

    private deleteWithConfirmation(onAccept: () => Promise<any>) {
        this.confirmationService.confirm({
            message: "Are you sure you want to remove this user?",
            accept: async () => {
                await onAccept();
            }
        });
    }

    private restoreWithConfirmation(onAccept: () => Promise<any>) {
        this.confirmationService.confirm({
            message: "Are you sure you want to restore this user?",
            accept: async () => {
                await onAccept();
            }
        });
    }

    private async runUserAction(action: () => Promise<any>) {
        this.runningUserAction = true;
        try {
            await action();
        } finally {
            this.runningUserAction = false;
        }
    }

    setDefaultPermissions(resetPermissions: boolean) {
        let roleDefaults =
            getDefaultPermissions(
                this.currentUserAccess,
                this.permissionOptions,
                this.defaultPermissions.defaultPermissionsByRole,
                this.messageService,
                true
            ) ?? [];

        if (this.shouldIncludeDefaultFormsPermissions()) {
            roleDefaults = this.mergeDefaultFormsPermissions(roleDefaults);
        }

        const { permissions, nextDefaultPermissionIds } = resolvePermissionsOnRoleChange(
            roleDefaults,
            this.userOrganizationPermissions,
            this.lastRoleDefaultPermissionIds,
            resetPermissions,
            (this.userOrganizationRoles ?? []).length > 0
        );

        this.lastRoleDefaultPermissionIds = nextDefaultPermissionIds;
        this.userOrganizationPermissions = permissions;
    }

    private seedLastRoleDefaultPermissionIds(): void {
        let roleDefaults =
            getDefaultPermissions(
                this.currentUserAccess,
                this.permissionOptions,
                this.defaultPermissions.defaultPermissionsByRole,
                this.messageService,
                true
            ) ?? [];

        if (this.shouldIncludeDefaultFormsPermissions()) {
            roleDefaults = this.mergeDefaultFormsPermissions(roleDefaults);
        }

        this.lastRoleDefaultPermissionIds = new Set(roleDefaults.map(permission => permission.id));
    }

    private shouldIncludeDefaultFormsPermissions(): boolean {
        if (!this.isFormsEnabledForCurrentOrg()) {
            return false;
        }

        const roles = this.userOrganizationRoles ?? [];
        if (roles.length === 0) {
            return false;
        }

        const disabledRolesForForms: string[] = [CoreRole.GenericLogin, CoreRole.GenericPatientLogin, CoreRole.ClinicalSafetyOfficer];
        if (roles.every(role => disabledRolesForForms.includes(role.name))) {
            return false;
        }

        return roles.some(role => role.name !== CoreRole.OrganisationAdmin);
    }

    private isFormsEnabledForCurrentOrg(): boolean {
        const orgId = this.currentUserAccess?.organization?.id;
        return this.organizations?.find(organization => organization.id === orgId)?.isFormsEnabled ?? false;
    }

    private mergeDefaultFormsPermissions(permissions: IPermission[]): IPermission[] {
        const formPermissions = [Permissions.WriteFormsInstances, Permissions.ReadFormsInstances]
            .map(name => this.permissionOptions.find(permission => permission.name === name))
            .filter((permission): permission is IPermission => !!permission);

        const mergedPermissions = [...permissions];
        for (const formPermission of formPermissions) {
            if (!mergedPermissions.some(permission => permission.id === formPermission.id)) {
                mergedPermissions.push(formPermission);
            }
        }

        return mergedPermissions;
    }

    private async initWidgets(): Promise<void> {
        this.permissionsLoading = true;

        try {
            const { organizationFormPermissions, defaultPermissions, permissionsDependencies } =
                await this.userEditDataService.loadUserEditData(this.userId);

            this.permissionsDependencies = permissionsDependencies;
            this.organizationFormPermissions = organizationFormPermissions;
            this.defaultPermissions = defaultPermissions;

            this.updateRoles().then();
            this.createRoleOptions(this.appRoles);
            this.updateFormPermissionDependencies();
            this.permissionOptions = this.createPermissionOptions();
        } finally {
            this.permissionsLoading = false;
        }
    }

    private async refreshUser() {
        const refreshedUser = await this.userService.get(this.userId);

        refreshedUser.dateOfBirth = SetLocalTimeZone(refreshedUser.dateOfBirth);
        this.patchUserForm(refreshedUser);
        this.currentUserName = refreshedUser.userName ?? "";
        this.userAccesses = refreshedUser.userAccesses.filter(ua => !ua.isArchived && ua.roles && ua.roles.length > 0 );
        this.initialUserAccesses = [...this.userAccesses];
        this.currentUserAccess = this.userAccesses[0];
        this.hiddenRoleObjects = this.currentUserAccess.roles.filter(r => this.hiddenRoles.includes(r.name));
        this.currentUserAccess.roles = this.currentUserAccess.roles.filter(r => !this.hiddenRoles.includes(r.name));
        this.userSsoProvider = refreshedUser.ssoProvider;
        this.isGenericLogin = checkGenericLoginRole(this.userName, this.userOrganizationRoles);
        this.defaultGenericPatient = checkGenericPatientLoginRole(this.userName, this.userOrganizationRoles);
        this.defaultClinicalSafetyOfficer = this.isClinicalSafetyOfficer = this.userOrganizationRoles?.some(
            r => r.name === CoreRole.ClinicalSafetyOfficer
        );
        this._prnRequired = refreshedUser.registrationNumberInfo?.required;
        this.userRoles = refreshedUser.registrationNumberInfo?.roles;
        this.userPermissions = refreshedUser.registrationNumberInfo?.permissions;

        await this.refreshButtons(refreshedUser.accountStatus);
    }

    private patchUserForm(userLogin?: UserLogin) {
        if (!!userLogin) {
            this.userForm.patchValue({
                user: userLogin,
                otherFields: userLogin,
                isAutoSaveFormOn: this.userService.currentUser?.autoSaveForms
            });
        }
    }

    autoSaveUser = async () => {
        if (this.isAutoSaveFormOn) {
            await this.save();
        }
        this.autosaveComplete = true;
    };

    private patchUserAccesses() {
        this.userForm.get("otherFields")?.patchValue({
            userAccesses: this.userAccesses,
        });

        this._prnRequired =
            this.userRoles.some(r => r === CoreRole.Doctor) || this.userPermissions.some(p => p === Permissions.WritePrescriptions);
    }

    private buildUserAccessesForSave(): UserAccess[] {
        const currentOrgId = this.currentUserAccess?.organization?.id;
        const currentOrgRoles = this.userOrganizationRoles || [];

        return this.userAccesses.map(ua => {
            const combinedRoles =
                ua.organization.id === currentOrgId
                    ? [
                          ...currentOrgRoles,
                          ...this.hiddenRoleObjects.filter(hiddenRole =>
                              !currentOrgRoles.some(r => r.id === hiddenRole.id)
                          )
                      ]
                    : ua.roles ?? [];

            return {
                ...ua,
                roles: combinedRoles,
                permissions: [...(ua.permissions ?? [])]
            };
        });
    }

    private syncCurrentPermissionsToUserAccesses(): void {
        const orgId = this.currentUserAccess?.organization?.id;
        if (!orgId) {
            return;
        }

        const userAccess = this.userAccesses.find(ua => ua.organization.id === orgId);
        if (!userAccess) {
            return;
        }

        userAccess.permissions = [...(this.currentUserAccess.permissions ?? [])];
        if (userAccess !== this.currentUserAccess) {
            this.currentUserAccess = userAccess;
        }
    }

    private syncCurrentPermissionsFromMultiselect(): void {
        if (this.isGenericLogin || this.isClinicalSafetyOfficer || !this.currentUserAccess?.organization?.id) {
            return;
        }

        const multiselectValue = this.userPermissionsMultiselectComponent?.value as IPermission[] | undefined;
        if (!multiselectValue) {
            return;
        }

        const orgId = this.currentUserAccess.organization.id;
        const userAccess = this.userAccesses.find(ua => ua.organization.id === orgId);
        if (!userAccess) {
            return;
        }

        userAccess.permissions = [...multiselectValue];
        if (userAccess !== this.currentUserAccess) {
            this.currentUserAccess.permissions = userAccess.permissions;
        }
    }

    private async refreshButtons(accountStatus: AccountStatus) {
        this.enableSuspendBtn = accountStatus == AccountStatus.Active || accountStatus == AccountStatus.Unverified;
        this.enableApproveBtn = accountStatus == AccountStatus.Unapproved;
        this.enableUnsuspendBtn = accountStatus == AccountStatus.Suspended;
        this.enableDeleteBtn = accountStatus != AccountStatus.Deleted && this.userHelperService.hasPermission(Permissions.ArchiveUser);
        this.enableRestoreBtn = accountStatus == AccountStatus.Deleted;
        this.enableResendInvitationBtn = accountStatus == AccountStatus.Invited;
        this.enableResendEmailConfirmationBtn = accountStatus == AccountStatus.Unverified;
        this.enableSendPasswordResetMailBtn = accountStatus == AccountStatus.Active;
        this.enableSendPinResetMailBtn = accountStatus == AccountStatus.Active;

        this.enableImpersonateBtn =
            this.userId && this.currentUserAccess?.organization?.id
                ? await this.userService.canCurrentUserImpersonateUser(this.userId, this.currentUserAccess?.organization?.id)
                : false;
    }

    get roleOptionsReadyAndNotEmpty(): boolean {
        return !!this.roleOptions?.length;
    }

    async addOrganization() {
        const newOrg = await this.organizationService.get(this.newOrganization);
        if (!newOrg) {
            return;
        }

        this.organizationFormPermissions[this.newOrganization] = await this.permissionService.getOrganizationFormPermissions(this.newOrganization);
        this.updateFormPermissionDependencies();

        const userAccess: UserAccess = {
            organization: newOrg,
            roles: [],
            permissions: [],
            groups: [],
            squads: [],
            isArchived: false
        };

        this.userAccesses = [...this.userAccesses, userAccess];
        this.currentUserAccess = userAccess;
        this.patchUserAccesses();
        this.onOrganisationChanged();

        this.organizationsOptions = this.organizationsOptions.filter(x => x.value !== this.newOrganization);

        this.newOrganization = null;
        this.displayAddOrganization = false;
    }


    removeOrganization() {
        const actualOrg = this.currentUserAccess.organization;

        const index = this.userAccesses.findIndex(x => x.organization.id == actualOrg.id);
        this.userAccesses.splice(index, 1);
        this.currentUserAccess = this.userAccesses[0];
        this.patchUserAccesses();
        this.organizationsOptions.push(<SelectItem>{label: actualOrg.name, value: actualOrg.id});
    }

    onUserPermissionChange(item: MultiSelectChangeEvent) {
        const selectedPermissions = (item.value ?? []) as IPermission[];
        this.userOrganizationPermissions = [...selectedPermissions];

        if (!item.itemValue) return;

        const isItemRemoved = !selectedPermissions.some(permission => permission.id === item.itemValue.id);
        this.triggerDependenciesForItem(item.itemValue.name, isItemRemoved);
    }

    private triggerDependenciesForItem(permissionName: string, isRemoved: boolean) {
        if (!this.permissionsDependencies[permissionName] || !this.userPermissionsMultiselectComponent) return;

        let options = this.userPermissionsMultiselectComponent.options ?? [];
        let values = [...(this.userOrganizationPermissions ?? [])];

        const dependentPermissionsFamilies = this.permissionsDependencies[permissionName];

        const permissions = getDefaultPermissions(
            this.currentUserAccess,
            this.permissionOptions,
            this.defaultPermissions.defaultPermissionsByRole,
            this.messageService,
            true
        );

        const dependentPermissionsNames = [];
        dependentPermissionsFamilies.forEach(element => {
            let permission = element.length != 1 ? element.find(x => permissions.find(y => y.name == x)) ?? element[0] : element[0];
            if (permission) {
                dependentPermissionsNames.push(permission);
            }
        });

        let valuesToChange = options?.filter(x => dependentPermissionsNames.includes(x.name));

        if (isRemoved) {
            options.filter(option => dependentPermissionsFamilies.flatMap(x => x).some(value => option.name == value))
                .forEach(option => (option.disabled = false));
        } else {
            let valuesToAdd = valuesToChange.filter(x => !values.some(value => value.id == x.id));
            values.push(...valuesToAdd);

            options.filter(option => valuesToChange.some(value => option.id == value.id)).forEach(option => (option.disabled = true));
        }

        this.userPermissionsMultiselectComponent.options = options;
        this.userOrganizationPermissions = [...values];
    }

    private async updateRoles() {
        const organizationId = this.currentUserAccess?.organization?.id;
        this.appRoles = [
            ...(organizationId
                ? await this.roleService.getOrganizationRoles(organizationId)
                : await this.roleService.getCurrentOrganizationRoles())
        ];
    }

    private createPermissionOptions() {
        const orgId = this.currentUserAccess?.organization?.id;
        const currentOrg = this.organizations?.find(o => o.id === orgId);
        const enabledModulesDict = currentOrg?.enabledModules ?? {};

        const enabledModuleNames = Object.entries(enabledModulesDict)
            .filter(([_, enabled]) => enabled)
            .map(([moduleName]) => moduleName);

        const currentOrgFormPermissions = orgId
            ? this.organizationFormPermissions?.[orgId] ?? []
            : [];

        let permissions = [
            ...(this.defaultPermissions?.allDefaultPermissions ?? []),
            ...currentOrgFormPermissions
        ];

        permissions = permissions.filter(permission => {
            const associatedModules = Object.entries(this.ModulePermissionMap)
                .filter(([_, perms]) => perms.includes(permission.name as Permissions))
                .map(([module]) => module);

            if (associatedModules.length === 0) {
                return true;
            }

            return associatedModules.every(module => enabledModuleNames.includes(module));
        });

        const permissionsDependencies = this.permissionsDependencies ?? {};
        const flatPermissionsDependencies = Object.keys(permissionsDependencies).flatMap(key =>
            permissionsDependencies[key]
                .flatMap(x => x)
                .map(permission => ({
                    masterPermission: key,
                    dependentPermission: permission
                }))
        );

        const checkIfEnabledInPairWithMasterPermission = (dependentPermissionName: string): boolean => {
            const masterPermissionName = flatPermissionsDependencies.find(
                x => x.dependentPermission === dependentPermissionName
            )?.masterPermission;
            const userPermissionsNames = this.userOrganizationPermissions?.map(x => x.name) ?? [];
            return userPermissionsNames.includes(masterPermissionName) && userPermissionsNames.includes(dependentPermissionName);
        };

        return permissions.map(p => ({
            ...p,
            disabled: p.name === Permissions.RegisterPatient ||
                      checkIfEnabledInPairWithMasterPermission(p.name)
        }));
    }


    private ModulePermissionMap: { [key in MiProModules]?: Permissions[] } = {
        [MiProModules.Consultations]: [
            Permissions.ConsultationHistoryReadUnrestricted,
            Permissions.ConsultationHistoryReadLimited,
            Permissions.ConsultationHistoryReadUnrestrictedExceptNotes,
            Permissions.ConsultationHistoryWrite,
            Permissions.ConsultationHistoryPrivateEpisodeRead,
            Permissions.ConsultationHistoryPrivateEpisodeWrite,
            Permissions.ConsultationHistoryReadPrivateEpisodesUnrestricted,
            Permissions.ConsultationHistoryWritePrivateEpisodesUnrestricted
        ],
        [MiProModules.Diary]: [
            Permissions.ReadDiary,
            Permissions.WriteDiary,
            Permissions.WriteIncidents
        ],
        [MiProModules.Prescriptions]: [
            Permissions.ReadPrescriptions,
            Permissions.WritePrescriptions
        ],
        [MiProModules.FunctionalGut]: [
            Permissions.ReadFunctionalGut,
            Permissions.WriteFunctionalGut
        ],
        [MiProModules.Forms]: [
            Permissions.ReadFormsInstances,
            Permissions.WriteFormsInstances,
            Permissions.WriteFormsDesign
        ],
        [MiProModules.Raven]: [
            Permissions.EditRavenConfig
        ],
        [MiProModules.GlobalDRO]: [
            Permissions.AccessGlobalDRO
        ],
        [MiProModules.NHS]: [
            Permissions.ReadNHSData
        ],
        [MiProModules.EventsModule]: [
            Permissions.WriteIncidents
        ],
        // Currently no permissions provided for these modules, but explicitly listed for clarity.
        [MiProModules.Tues]: [],
        [MiProModules.Referrals]: [],
        [MiProModules.Investigations]: [],
        [MiProModules.Reporting]: []
    };

    private updateFormPermissionDependencies() {
        const allFormPermissions = Object.keys(this.organizationFormPermissions).flatMap(
            (k: string) => this.organizationFormPermissions[k]
        );
        allFormPermissions
            .filter(fp => fp.dependantOnPermissionId)
            .forEach(fp => {
                const permDependentOn = allFormPermissions.find(fp2 => fp2.id == fp.dependantOnPermissionId);
                if (permDependentOn) {
                    this.permissionsDependencies[permDependentOn.name] = [[fp.name]];
                }
            });
    }
}
